import useSWRInfinite from 'swr/infinite';

// logic
import { SETTINGS } from '../../constants';
// api
import getTicketsAPICall from '../../api/getTickets';
import { useState } from 'react';
import moment from 'moment';
import { mapGroupsToCountryCodes } from '../../mappers/country-groups';

const vehicleGroups = {
  LTL: [
    'Small van (max. 400kg)',
    'Van (max. 800kg)',
    'Box Van (max. 800kg)',
    'Sprinter - Large van (max. 1200kg)',
    'Light rigid (max. 3.5Tn)',
    'Heavy rigid (max. 20Tn)',
  ],
  FTL: ['FTL (max. 24Tn) Tautliner Standard', 'FTL (max. 24Tn) Tautliner Mega'],
};

// calls api to get tickets
const ticketsFetcher = async (...args) => {
  return getTicketsAPICall(...args);
};

const getSearchQueryFromFilters = (filters) => {
  try {
    const { search } = filters;
    const isSearchValid = search.trim() !== '' && search.length > 0;
    if (!isSearchValid) return null;
    const query = `q={"name": "search", "op": "raw", "value": "'${search}':*"}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getCreatedAtAfterFromFilters = (filters) => {
  try {
    const { created_at_after } = filters;
    const momentDate = moment(created_at_after);
    const isCreatedAtAfterValid = momentDate.isValid();
    if (!isCreatedAtAfterValid) return null;
    const dateFormated = momentDate.format('YYYY-MM-DDTHH:mm:ss');
    const query = `created_at_after=${dateFormated}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getStateQueryFromFilters = (filters) => {
  try {
    const { state } = filters;
    if (state.length === 0) return null;
    const stateJoined = state.map((state) => `"${state}"`).join(', ');
    const query = `char={"name": "synthetic_state", "op": "in", "value": [${stateJoined}]}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getAssignedToQueryFromFilters = (filters) => {
  try {
    const { assigned_to } = filters;
    if (assigned_to.length === 0) return null;
    let query = `assigned_to_or_empty=${assigned_to[0]}`;
    if (assigned_to.length > 1) {
      for (let i = 1; i < assigned_to.length; i++) {
        query += `&assigned_to_or_empty=${assigned_to[i]}`;
      }
    }
    return query;
  } catch (e) {
    return null;
  }
};

const getShipperIdQueryFromFilters = (filters) => {
  try {
    const { shipper_id } = filters;
    if (shipper_id.length === 0) return null;
    let query = `shipper_id=${shipper_id[0]}`;
    if (shipper_id.length > 1) {
      for (let i = 1; i < shipper_id.length; i++) {
        query += `&shipper_id=${shipper_id[i]}`;
      }
    }
    return query;
  } catch (e) {
    return null;
  }
};

const getWorkingAreaIdQueryFromFilters = (filters) => {
  try {
    const { working_area_id } = filters;
    if (working_area_id.length === 0) return null;
    let query = `working_area=${working_area_id[0]}`;
    if (working_area_id.length > 1) {
      for (let i = 1; i < working_area_id.length; i++) {
        query += `&working_area=${working_area_id[i]}`;
      }
    }
    return query;
  } catch (e) {
    return null;
  }
};

const getOriginCountryCodeQueryFromFilters = (filters) => {
  try {
    const { origin_country_group } = filters;
    if (!origin_country_group || origin_country_group.length === 0) return null;
    const countryCodes = mapGroupsToCountryCodes(origin_country_group);
    const countryCodesJoined = countryCodes.map((country) => `"${country}"`).join(', ');
    const query = `char={"name": "origin_country_code", "op": "in", "value": [${countryCodesJoined}]}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getDestinationCountryCodeQueryFromFilters = (filters) => {
  try {
    const { destination_country_group } = filters;
    if (!destination_country_group || destination_country_group.length === 0) return null;
    const countryCodes = mapGroupsToCountryCodes(destination_country_group);
    const countryCodesJoined = countryCodes.map((country) => `"${country}"`).join(', ');
    const query = `char={"name": "destination_country_code", "op": "in", "value": [${countryCodesJoined}]}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getVehicleTypeQueryFromFilters = (filters) => {
  try {
    const { vehicle_type } = filters;
    if (vehicle_type.length === 0) return null;
    const vehicleTypeCodes = vehicle_type
      .map((group) => {
        return vehicleGroups[group];
      })
      .flat();
    const vehicleTypeCodesJoined = vehicleTypeCodes
      .map((vehicleTypeCode) => `"${vehicleTypeCode}"`)
      .join(', ');
    const query = `char={"name": "vehicle_type", "op": "in", "value": [${vehicleTypeCodesJoined}]}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getPageSizeQueryFromSettings = () => {
  const page_size = SETTINGS.TICKETS_LIST_PAGE_SIZE;
  const query = `page_size=${page_size}`;
  return query;
};

const getTicketsQueryFromFilters = (filters) => {
  let query = ``;
  const pageSizeQuery = getPageSizeQueryFromSettings();
  const searchQuery = getSearchQueryFromFilters(filters);
  const createdAtAfterQuery = getCreatedAtAfterFromFilters(filters);
  const stateQuery = getStateQueryFromFilters(filters);
  const assignedToQuery = getAssignedToQueryFromFilters(filters);
  const originCountryCodeQuery = getOriginCountryCodeQueryFromFilters(filters);
  const destinationCountryCodeQuery = getDestinationCountryCodeQueryFromFilters(filters);
  const shipperIdQuery = getShipperIdQueryFromFilters(filters);
  const vehicleTypeQuery = getVehicleTypeQueryFromFilters(filters);
  const workingAreaIdQuery = getWorkingAreaIdQueryFromFilters(filters);
  const queries = [
    pageSizeQuery,
    searchQuery,
    createdAtAfterQuery,
    stateQuery,
    assignedToQuery,
    originCountryCodeQuery,
    destinationCountryCodeQuery,
    shipperIdQuery,
    vehicleTypeQuery,
    workingAreaIdQuery,
  ];
  queries.forEach((currentQuery, index) => {
    if (!!currentQuery) {
      const paramSeparator = index === 0 ? '?' : '&';
      query = `${query}${paramSeparator}${currentQuery}`;
    }
  });
  return query;
};

// returns tickets with loading/reloading state
const useTicketsList = (props) => {
  const { selectedFilters } = props;
  const [lastUpdate, setLastUpdate] = useState(null);

  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.length) return null; // reached the end
    const query = `${getTicketsQueryFromFilters(selectedFilters)}&page=${pageIndex + 1}`;
    return `tickets/${query}`;
  };

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {
    data: ungroupedTickets,
    error: ticketsError,
    isValidating: isReloadingTickets,
    size,
    setSize,
    mutate,
  } = useSWRInfinite(getKey, ticketsFetcher, {
    refreshInterval: SETTINGS.RELOAD_TICKETS_LIST_INTERVAL_IN_SECONDS * 1000,
    revalidateAll: true,
    revalidateIfStale: true,
    parallel: true,
    onSuccess: () => {
      const nowTime = moment().format('HH:mm:ss');
      setLastUpdate(nowTime);
      setIsLoadingMore(false);
    },
  });

  const isEmpty = ungroupedTickets?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty ||
    (ungroupedTickets &&
      ungroupedTickets[ungroupedTickets.length - 1]?.length < SETTINGS.TICKETS_LIST_PAGE_SIZE);

  const isLoadingTickets = typeof ungroupedTickets === 'undefined';

  const loadMore = () => {
    setIsLoadingMore(true);
    return setSize(size + 1);
  };

  const reset = () => {
    mutate();
  };

  return {
    ungroupedTickets,
    isEmpty,
    isReachingEnd,
    isLoadingMore,
    lastUpdate,
    // first load
    isLoadingTickets,
    // reloads
    isReloadingTickets,
    ticketsError,
    loadMore,
    reset,
  };
};

export default useTicketsList;
